import React, { useState, Fragment, useEffect } from "react";
import {
  Row,
  Col,
  Card,
  Table,
  Button,
  Form,
  InputGroup,
  Accordion,
  Spinner,
  Modal,
} from "react-bootstrap";
import { List, X, Plus, Minus } from "react-feather";

import { useCart } from "../../contexts/cartContext";

import apiService from "../../services/apiService";
import { formatAmount } from "../../utils";
import { cartSession } from "../../utils/cart";

const CartItems = ({
  cart,
  setCart,
  showNotification,
  isAuthenticated,
  sessionId,
  couponCode,
  setCouponCode,
}) => {
  const { updateCartCount } = useCart();

  //const [couponCode, setCouponCode] = useState("");
  const [expandedRow, setExpandedRow] = useState(null); // Track which row is expanded
  const [loadingState, setLoadingState] = useState({ action: "", id: "" }); // Single loading state
  const [quantities, setQuantities] = useState({}); // Track quantities for custom items
  const [isPageLoading, setIsPageLoading] = useState(false);

  // Initialize quantities for custom items
  useEffect(() => {
    if (cart?.CartItems) {
      const initialQuantities = {};
      cart.CartItems.forEach((item) => {
        if (item.carModel === "Custom") {
          initialQuantities[item.id] = item.quantity;
        }
      });
      setQuantities(initialQuantities);

      // Update cart count whenever page load
      updateCartCount(cart.CartItems.length);
    }
  }, [cart, updateCartCount]);

  const handleQuantityChange = async (itemId, newQuantity) => {
    setLoadingState({ action: "update-quantity", id: itemId });
    try {
      let data = {
        quantity: newQuantity,
      };
      if (!isAuthenticated) data.sessionId = sessionId;

      const response = await apiService.put(`/cart/items/${itemId}`, data);
      setCart(response); // Update the cart state
    } catch (error) {
      showNotification(
        "danger",
        error.message || "Failed to update quantity of item in your cart.",
        5000
      );
    } finally {
      setLoadingState(null);
    }
  };

  const handleIncrement = async (itemId) => {
    // Find the item in cart.CartItems
    const item = cart?.CartItems?.find((i) => i.id === itemId);
    let increment = 1;
    if (item && item.colourType === "PEARL COAT WITH BASE WHITE") {
      increment = 2;
    }
    const newQuantity = (quantities[itemId] || 0) + increment;
    setQuantities((prev) => ({ ...prev, [itemId]: newQuantity }));
    await handleQuantityChange(itemId, newQuantity);
  };

  const handleDecrement = async (itemId) => {
    // Find the item in cart.CartItems
    const item = cart?.CartItems?.find((i) => i.id === itemId);
    let decrement = 1;
    if (item && item.colourType === "PEARL COAT WITH BASE WHITE") {
      decrement = 2;
    }
    const newQuantity = Math.max((quantities[itemId] || 0) - decrement, 1);
    setQuantities((prev) => ({ ...prev, [itemId]: newQuantity }));
    await handleQuantityChange(itemId, newQuantity);
  };

  const clearCart = async () => {
    setLoadingState({ action: "clear-all" });
    try {
      const apiURL = isAuthenticated ? `/cart` : `/cart?sessionId=${sessionId}`;
      const response = await apiService.delete(apiURL);
      setCart(response); // Update the cart state
      cartSession.clear();

      // Update cart count whenever cart changes
      updateCartCount(0);
    } catch (error) {
      showNotification(
        "danger",
        error.message || "Failed to remove cart.",
        5000
      );
    } finally {
      setLoadingState(null);
    }
  };

  const handleRemoveItem = async (itemId) => {
    setIsPageLoading(true);
    setLoadingState({ action: "delete", id: itemId });
    try {
      const apiURL = isAuthenticated
        ? `/cart/items/${itemId}`
        : `/cart/items/${itemId}?sessionId=${sessionId}`;
      const response = await apiService.delete(apiURL);
      setCart(response);

      // Update cart count whenever cart changes
      updateCartCount(response?.CartItems.length);

      if (response?.CartItems.length === 0) cartSession.clear();
    } catch (error) {
      showNotification(
        "danger",
        error.message || "Failed to remove item from your cart.",
        5000
      );
    } finally {
      setLoadingState(null);
      setIsPageLoading(false);
    }
  };

  const handleApplyDiscount = async () => {
    if (!couponCode) return;
    try {
      setIsPageLoading(true);
      const apiURL = isAuthenticated
        ? `/cart/apply-discount`
        : `/cart/apply-discount?sessionId=${sessionId}`;

      const response = await apiService.post(apiURL, {
        code: couponCode,
      });

      setCart(response);
    } catch (error) {
      console.error("Error applying discount:", error);
      alert("Invalid or expired coupon code.");
    } finally {
      setIsPageLoading(false);
    }
  };

  const handleRemoveDiscount = async () => {
    try {
      setIsPageLoading(true);

      const response = await apiService.post("/cart/remove-discount", {
        code: couponCode,
      });
      setCart(response);
      setCouponCode("");
      sessionStorage.removeItem("couponCode");
    } catch (error) {
      console.error("Error removing discount:", error);
    } finally {
      setIsPageLoading(false);
    }
  };

  const toggleAccordion = (itemId) => {
    setExpandedRow(expandedRow === itemId ? null : itemId); // Toggle accordion
  };

  const renderSavings = (percentageDiscount, fixedDiscount) => {
    if (percentageDiscount > 0 || fixedDiscount > 0) {
      return (
        <div className="text-success fw-medium mt-2">
          <span className="badge bg-primary fs-medium fw-medium">
            SAVE{" "}
            {percentageDiscount > 0
              ? `${formatAmount(percentageDiscount)}%`
              : ""}{" "}
            {percentageDiscount > 0 && fixedDiscount > 0 && " + "}
            {fixedDiscount > 0 && (
              <Fragment>
                <span className="currency">&#8377;</span>
                {formatAmount(fixedDiscount)}
              </Fragment>
            )}
          </span>
        </div>
      );
    }
    return null;
  };

  const renderPrice = (item, productName, quantity) => {
    // Product configuration mapping
    const productConfig = {
      "Colour Coat": {
        pricePerQuantityKey: "colorCoatPricePerQuantity",
        totalPriceKey: "colorCoatTotalPrice",
        discountFilter: (x) =>
          x.name !== "Clear Coat" &&
          x.name !== "Primer" &&
          x.name !== "PU Hardener",
      },
      "Clear Coat": {
        pricePerQuantityKey: "clearCoatPricePerQuantity",
        totalPriceKey: "clearCoatTotalPrice",
        discountFilter: (x) => x.name === "Clear Coat",
      },
      Primer: {
        pricePerQuantityKey: "primerPricePerQuantity",
        totalPriceKey: "primerTotalPrice",
        discountFilter: (x) => x.name === "Primer",
      },
      "PU Hardener": {
        pricePerQuantityKey: "puHardenerPricePerQuantity",
        totalPriceKey: "puHardenerTotalPrice",
        discountFilter: (x) => x.name === "PU Hardener",
      },
    };

    const config = productConfig[productName];
    if (!config) return null;

    // Calculate prices
    const originalPrice = item[config.pricePerQuantityKey] * quantity;
    const discountPrice = item[config.totalPriceKey];

    // Get discount details
    const discountDetails = item.discountDetails.filter(
      config.discountFilter
    )[0];
    const totalPercentageDiscount =
      discountDetails?.totalPercentageDiscount || 0;
    const totalFixedDiscount = discountDetails?.totalFixedDiscount || 0;

    return (
      <div>
        {discountPrice < originalPrice ? (
          <>
            <span className="text-decoration-line-through text-muted me-2">
              <span className="currency">&#8377;</span>
              {formatAmount(originalPrice)}
            </span>
            <span className="text-primary fw-bold">
              <span className="currency">&#8377;</span>
              {formatAmount(discountPrice)}
            </span>
            {renderSavings(totalPercentageDiscount, totalFixedDiscount)}
          </>
        ) : (
          <span className="text-primary fw-bold">
            <span className="currency">&#8377;</span>
            {formatAmount(originalPrice)}
          </span>
        )}
      </div>
    );
  };

  const renderDefaultProductSection = (
    item,
    productName,
    totalPriceKey,
    defaultPack = "1"
  ) => {
    if (item[totalPriceKey] <= 0) return null;

    const product = item.defaultProducts.find((p) => p.name === productName);

    return (
      <Fragment>
        <Row className="align-items-center">
          <Col xs={"auto"}>
            <h6 className="fw-medium">{productName}</h6>
          </Col>
          <Col>
            <hr />
          </Col>
        </Row>
        <Row className="mt-3">
          <Col xs={"auto"} className="mb-3">
            <label className="d-block text-light-emphasis mb-1">Quantity</label>
            <label className="fw-medium">
              {product?.quantity || 0} Pack (
              {(product?.pack || defaultPack) + "L"}/per pack)
            </label>
          </Col>
          <Col xs={"auto"} className="mb-3">
            <label className="d-block text-light-emphasis mb-1">Details</label>
            <label className="fw-medium">{product?.details}</label>
          </Col>
          <Col xs={"auto"} className="mb-3">
            <label className="d-block text-light-emphasis mb-1">Amount</label>
            {renderPrice(item, productName, product?.quantity || 0)}
          </Col>
        </Row>
      </Fragment>
    );
  };

  const getDefaultProductQuantity = (item, productName) => {
    const product = item.defaultProducts.find((p) => p.name === productName);
    return product && product.pricePerQuantity > 0 ? product.quantity : 0;
  };

  const getTotalPrice = (item) => {
    const totalDiscountPrice =
      Number(item.colorCoatTotalPrice || 0) +
      Number(item.clearCoatTotalPrice || 0) +
      Number(item.primerTotalPrice || 0) +
      Number(item.puHardenerTotalPrice || 0);

    const totalOriginalPrice =
      Number(item.colorCoatPricePerQuantity * item.quantity || 0) +
      Number(
        item.clearCoatPricePerQuantity *
          getDefaultProductQuantity(item, "Clear Coat") || 0
      ) +
      Number(
        item.primerPricePerQuantity *
          getDefaultProductQuantity(item, "Primer") || 0
      ) +
      Number(
        item.puHardenerPricePerQuantity *
          getDefaultProductQuantity(item, "PU Hardener") || 0
      );

    return (
      <div>
        {totalDiscountPrice < totalOriginalPrice ? (
          <>
            <span className="text-decoration-line-through text-muted me-2">
              <span className="currency">&#8377;</span>
              {formatAmount(totalOriginalPrice)}
            </span>
            <span className="text-primary fw-bold">
              <span className="currency">&#8377;</span>
              {formatAmount(totalDiscountPrice)}
            </span>
          </>
        ) : (
          <span className="text-primary fw-bold">
            <span className="currency">&#8377;</span>
            {formatAmount(totalOriginalPrice)}
          </span>
        )}
      </div>
    );
  };

  return (
    <Fragment>
      <div>
        <Button
          variant="outline-secondary"
          onClick={clearCart}
          className="mb-3"
          disabled={loadingState?.action === "clear-all"}
        >
          {loadingState?.action === "clear-all" ? (
            <Spinner animation="border" role="status">
              <span className="visually-hidden">Loading...</span>
            </Spinner>
          ) : (
            "Clear All"
          )}
        </Button>
      </div>
      <Card className="bg-white px-3 py-4 rounded-0 border-0 items">
        <Table responsive className="align-middle">
          <thead>
            <tr>
              <th>Car Model</th>
              <th>Colour Name</th>
              <th>Colour Type</th>
              <th>Colour Coat</th>
              <th>Clear Coat</th>
              <th>Primer</th>
              <th>PU Hardener</th>
              <th>Amount</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {cart.CartItems.map((item) => (
              <Fragment key={item.id}>
                <tr>
                  <td>{item.carModel}</td>
                  <td>
                    <div className="d-flex gap-2 align-items-center">
                      <div
                        className="product rounded-circle"
                        style={{
                          backgroundColor: item.Product.hexCode,
                        }}
                      ></div>
                      <span>{item.Product.name}</span>
                    </div>
                  </td>
                  <td>{item.colourType}</td>
                  <td>
                    {item.quantity}L
                    {item.carModel === "Custom" && (
                      <div className="d-inline-flex align-items-center border rounded-2 ms-2 quantity">
                        <Button
                          variant="link"
                          onClick={() => handleDecrement(item.id)}
                          className="text-dark"
                          disabled={loadingState?.action === "update-quantity"}
                        >
                          {loadingState?.action === "update-quantity" &&
                          loadingState.id === item.id ? (
                            <Spinner animation="border" role="status" size="sm">
                              <span className="visually-hidden">
                                Loading...
                              </span>
                            </Spinner>
                          ) : (
                            <Minus width={12} />
                          )}
                        </Button>
                        <span className="mx-1 fs-medium">
                          {quantities[item.id] || item.quantity}
                        </span>
                        <Button
                          variant="link"
                          onClick={() => handleIncrement(item.id)}
                          className="text-dark"
                          disabled={loadingState?.action === "update-quantity"}
                        >
                          {loadingState?.action === "update-quantity" &&
                          loadingState.id === item.id ? (
                            <Spinner animation="border" role="status" size="sm">
                              <span className="visually-hidden">
                                Loading...
                              </span>
                            </Spinner>
                          ) : (
                            <Plus width={14} />
                          )}
                        </Button>
                      </div>
                    )}
                  </td>
                  <td>{getDefaultProductQuantity(item, "Clear Coat")} Pack</td>
                  <td>{getDefaultProductQuantity(item, "Primer")} Pack</td>
                  <td>{getDefaultProductQuantity(item, "PU Hardener")} Pack</td>
                  <td>{getTotalPrice(item)}</td>
                  <td>
                    <div className="d-flex gap-2 action">
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        className="rounded-circle p-0"
                        onClick={() => toggleAccordion(item.id)}
                      >
                        <List size={14} />
                      </Button>
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        className="rounded-circle p-0"
                        onClick={() => handleRemoveItem(item.id)}
                        disabled={
                          loadingState?.action === "delete" &&
                          loadingState.id === item.id
                        }
                      >
                        {loadingState?.action === "delete" &&
                        loadingState.id === item.id ? (
                          <Spinner animation="border" role="status">
                            <span className="visually-hidden">Loading...</span>
                          </Spinner>
                        ) : (
                          <X size={14} />
                        )}
                      </Button>
                    </div>
                  </td>
                </tr>
                {expandedRow === item.id && (
                  <tr>
                    <td colSpan={7}>
                      <Accordion defaultActiveKey={item.id}>
                        <Accordion.Item eventKey={item.id}>
                          <Accordion.Body>
                            <Row className="align-items-center">
                              <Col xs={"auto"}>
                                <h6 className="fw-medium">
                                  {/* Capitalize each word in colourType */}
                                  {item.colourType
                                    .toLowerCase()
                                    .replace(/\b\w/g, (c) => c.toUpperCase())}
                                </h6>
                              </Col>
                              <Col>
                                <hr />
                              </Col>
                            </Row>
                            <Row className="mt-3">
                              <Col xs={"auto"} className="mb-3">
                                <label className="d-block text-light-emphasis mb-1">
                                  Car Model
                                </label>
                                <label className="fw-medium">
                                  {item.carModel}
                                </label>
                              </Col>
                              <Col xs={"auto"} className="mb-3">
                                <label className="d-block text-light-emphasis mb-1">
                                  Quantity
                                </label>
                                <label className="fw-medium">
                                  {item.quantity}L
                                </label>
                              </Col>
                              <Col xs={"auto"} className="mb-3">
                                <label className="d-block text-light-emphasis mb-1">
                                  Amount
                                </label>
                                {renderPrice(
                                  item,
                                  "Colour Coat",
                                  item.quantity
                                )}
                              </Col>
                            </Row>
                            {renderDefaultProductSection(
                              item,
                              "Clear Coat",
                              "clearCoatTotalPrice",
                              "1.5"
                            )}
                            {renderDefaultProductSection(
                              item,
                              "Primer",
                              "primerTotalPrice",
                              "1"
                            )}
                            {renderDefaultProductSection(
                              item,
                              "PU Hardener",
                              "puHardenerTotalPrice",
                              "0.5"
                            )}
                          </Accordion.Body>
                        </Accordion.Item>
                      </Accordion>
                    </td>
                  </tr>
                )}
              </Fragment>
            ))}
          </tbody>
        </Table>

        <Row className="mt-4 justify-content-end">
          <Col xs={"auto"}>
            <InputGroup>
              <Form.Control
                placeholder="Coupon code"
                aria-label="Coupon code"
                value={couponCode}
                onChange={(e) => setCouponCode(e.target.value)}
              />
              <Button variant="light" size="lg" onClick={handleApplyDiscount}>
                Apply Coupon
              </Button>
              {couponCode && (
                <Button
                  variant="light"
                  size="lg"
                  onClick={handleRemoveDiscount}
                >
                  Remove Coupon
                </Button>
              )}
            </InputGroup>
          </Col>
        </Row>
      </Card>
      <Modal
        show={isPageLoading}
        aria-labelledby="Loading"
        className="modal-loading"
        contentClassName="bg-transparent border-0"
        centered
      >
        <Modal.Body className="text-center">
          <Spinner animation="border" role="status" variant="primary">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
        </Modal.Body>
      </Modal>
    </Fragment>
  );
};

export default CartItems;
